/**
 * Local/dev sign-in picker. Shown after Log out so the demo can switch users
 * (Share / notifications) without silently re-authing as the previous account.
 */

import { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import { config } from '../config/environment'
import { SERIF_HEADING } from '../theme'
import { continueDevLoginAs } from '../hooks/useAuth'

interface DevUser {
  email: string
  name: string
}

export function DevLoginPicker() {
  const [users, setUsers] = useState<DevUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await fetch(`${config.API_CF_URL}/auth/dev-users`, { credentials: 'include' })
        if (!res.ok) throw new Error(`dev-users ${res.status}`)
        const data = (await res.json()) as { users?: DevUser[] }
        if (!cancelled) setUsers(data.users ?? [])
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', bgcolor: 'background.default', p: 2 }}>
      <Box
        sx={{
          width: '100%', maxWidth: 400, textAlign: 'center',
          px: { xs: 3.5, sm: 4.5 }, py: 5,
          bgcolor: 'background.paper',
          border: '1px solid', borderColor: 'divider', borderRadius: 4,
          boxShadow: '0 1px 3px rgba(40,36,31,0.04), 0 8px 30px rgba(40,36,31,0.06)',
        }}
      >
        <Typography variant="h5" sx={{ ...SERIF_HEADING, color: 'text.primary', mb: 1 }}>
          Sign in to Ros
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem', lineHeight: 1.5, mb: 3 }}>
          Pick a local user to continue. Use a different browser or incognito window to demo Share and notifications.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>{error}</Alert>}
        {loading ? (
          <CircularProgress size={22} />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {users.map((u) => (
              <Button
                key={u.email}
                variant="outlined"
                fullWidth
                onClick={() => continueDevLoginAs(u.email)}
                sx={{
                  py: 1.1,
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  borderColor: 'divider',
                  color: 'text.primary',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(0,143,168,0.08)' },
                }}
              >
                <Box sx={{ textAlign: 'left' }}>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>{u.name}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{u.email}</Typography>
                </Box>
              </Button>
            ))}
            {users.length === 0 && !error && (
              <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                No DEV_AUTH_USERS configured.
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}
